import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_HEADING,
  FONT_UI,
  SKIN_THEMES,
} from "../layouts/constants";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-control-geocoder/dist/Control.Geocoder.css";
import "leaflet-control-geocoder";
import logo from "../assets/image/eblackdog_sentinal.png";

/* =========================================================
   GOOGLE MAPS LOADER
========================================================= */
const GOOGLE_MAPS_SCRIPT_ID = "eblackdog-google-maps-script";
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY?.trim() || "";
const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 };
const DEFAULT_ZOOM = 5;
const SELECTED_LOCATION_ZOOM = 16;

let googleMapsLoadingPromise = null;

function findExistingGoogleMapsScript() {
  const scriptById = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
  if (scriptById) return scriptById;
  return document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
}

function getGoogleMapsErrorMessage(error) {
  const errorText = String(error?.message || error?.name || error || "");

  if (errorText.includes("RefererNotAllowedMapError")) {
    return "This website is not authorized for the Google Maps API key. Add this domain under the API key website restrictions.";
  }
  if (errorText.includes("InvalidKeyMapError")) {
    return "The Google Maps API key is invalid. Check REACT_APP_GOOGLE_MAPS_API_KEY.";
  }
  if (errorText.includes("ApiNotActivatedMapError")) {
    return "Maps JavaScript API is not enabled for this Google Cloud project.";
  }
  if (errorText.includes("BillingNotEnabledMapError")) {
    return "Billing is not enabled for the Google Cloud project used by this API key.";
  }
  if (errorText.includes("MissingKeyMapError")) {
    return "Google Maps API key is missing.";
  }

  return error?.message || "Google Maps JavaScript API could not be loaded.";
}

function loadGoogleMapsScript(apiKey) {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.reject(new Error("Google Maps can only be loaded in a browser."));
  }

  if (window.google?.maps) return Promise.resolve(window.google.maps);

  if (!apiKey) {
    return Promise.reject(
      new Error("Google Maps API key is missing. Add REACT_APP_GOOGLE_MAPS_API_KEY to the .env file.")
    );
  }

  if (googleMapsLoadingPromise) return googleMapsLoadingPromise;

  googleMapsLoadingPromise = new Promise((resolve, reject) => {
    let settled = false;

    const resolveOnce = () => {
      if (settled || !window.google?.maps) return;
      settled = true;
      resolve(window.google.maps);
    };

    const rejectOnce = (error) => {
      if (settled) return;
      settled = true;
      googleMapsLoadingPromise = null;
      reject(error instanceof Error ? error : new Error("Google Maps JavaScript API failed to load."));
    };

    const handleScriptLoad = () => {
      if (window.google?.maps) {
        resolveOnce();
        return;
      }
      rejectOnce(new Error("Google Maps script loaded, but the Maps API is unavailable."));
    };

    const handleScriptError = () => {
      rejectOnce(new Error("Google Maps JavaScript API network request failed."));
    };

    const previousAuthFailure = window.gm_authFailure;
    window.gm_authFailure = () => {
      if (typeof previousAuthFailure === "function") {
        try {
          previousAuthFailure();
        } catch (callbackError) {
          console.error("Previous Google Maps authentication callback failed:", callbackError);
        }
      }
      rejectOnce(
        new Error(
          "Google Maps authentication failed. Check the API key, enabled APIs, billing and website restrictions."
        )
      );
    };

    const existingScript = findExistingGoogleMapsScript();
    if (existingScript) {
      existingScript.addEventListener("load", handleScriptLoad, { once: true });
      existingScript.addEventListener("error", handleScriptError, { once: true });
      window.setTimeout(() => {
        if (window.google?.maps) resolveOnce();
      }, 0);
      return;
    }

    const script = document.createElement("script");
    script.id = GOOGLE_MAPS_SCRIPT_ID;

    const queryParameters = new URLSearchParams({
      key: apiKey,
      libraries: "places",
      v: "weekly",
    });

    script.src = `https://maps.googleapis.com/maps/api/js?${queryParameters.toString()}`;
    script.async = true;
    script.defer = true;
    script.setAttribute("data-google-maps-loader", "eblackdog");
    script.onload = handleScriptLoad;
    script.onerror = () => {
      script.remove();
      handleScriptError();
    };

    document.head.appendChild(script);
  });

  return googleMapsLoadingPromise;
}

function normalizeMapProvider(value) {
  const provider = String(value || "").trim().toLowerCase();
  if (provider === "google" || provider === "googlemaps" || provider === "google_maps") return "google";
  return "openstreetmap";
}

/* ========================================================= */

const initialForm = {
  run_id: "",
  member_id: "",
  title: "",
  vehicle: "",
  start_time: "",
  end_time: "",
  from_name: "",
  from_lat: "",
  from_lng: "",
  to_name: "",
  to_lat: "",
  to_lng: "",
};

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function valueOrEmpty(value) {
  return value === null || value === undefined ? "" : String(value);
}

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("adminToken")
  );
}

function getClientId() {
  return (
    localStorage.getItem("adminId") ||
    localStorage.getItem("admin_id") ||
    localStorage.getItem("clientId") ||
    localStorage.getItem("client_id") ||
    null
  );
}

function api(path) {
  const base = API_BASE_URL.replace(/\/$/, "");
  if (base.endsWith("/api") && path.startsWith("/api/")) {
    return `${base}${path.replace(/^\/api/, "")}`;
  }
  return `${base}${path}`;
}

function toDatetimeLocal(value) {
  if (!value) return "";

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";

  const pad = (n) => String(n).padStart(2, "0");

  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

function toGuardsArray(raw) {
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.guards)) return raw.guards;
  if (Array.isArray(raw?.data)) return raw.data;
  if (Array.isArray(raw?.list)) return raw.list;
  return [];
}

function createCustomIcon({
  label,
  bg = "#2563eb",
  border = "#ffffff",
  textColor = "#ffffff",
  isLogo = false,
  logoUrl = "",
}) {
  if (isLogo && logoUrl) {
    return L.divIcon({
      className: "custom-map-pin-wrapper",
      html: `
        <div style="
          position: relative;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: ${bg};
          border: 3px solid ${border};
          box-shadow: 0 4px 14px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        ">
          <img src="${logoUrl}" alt="logo" style="width: 28px; height: 28px; object-fit: contain;" />
        </div>
      `,
      iconSize: [40, 40],
      iconAnchor: [20, 40],
      popupAnchor: [0, -32],
    });
  }

  return L.divIcon({
    className: "custom-map-pin-wrapper",
    html: `
      <div style="
        width:30px;height:30px;border-radius:50% 50% 50% 0;
        transform:rotate(-45deg);background:${bg};border:2px solid ${border};
        box-shadow:0 4px 10px rgba(0,0,0,.25);
        display:flex;align-items:center;justify-content:center;
      ">
        <span style="
          transform:rotate(45deg);font-size:12px;font-weight:700;color:${textColor};
          font-family:Arial,sans-serif;
        ">${label}</span>
      </div>
    `,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -28],
  });
}

const fromIcon = createCustomIcon({ label: "F", bg: "#16a34a", isLogo: true, logoUrl: logo });
const toIcon = createCustomIcon({ label: "T", bg: "#dc2626", isLogo: true, logoUrl: logo });

/* =========================================================
   GOOGLE MAPS CUSTOM ICONS (canvas – logo + color)
========================================================= */

/**
 * Creates a circular Google Maps icon with the company logo
 * on a colored background (used for From / To).
 */
function createGoogleLogoIcon(bgColor, logoUrl, size = 44) {
  return new Promise((resolve) => {
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");

    // Outer colored circle
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2 - 2, 0, Math.PI * 2);
    ctx.fillStyle = bgColor;
    ctx.fill();

    // White border
    ctx.lineWidth = 3;
    ctx.strokeStyle = "#ffffff";
    ctx.stroke();

    // Soft shadow ring
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2 - 1.5, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(0,0,0,0.25)";
    ctx.lineWidth = 1;
    ctx.stroke();

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = logoUrl;

    img.onload = () => {
      const logoSize = Math.round(size * 0.62);
      const offset = (size - logoSize) / 2;
      ctx.drawImage(img, offset, offset, logoSize, logoSize);
      resolve({
        url: canvas.toDataURL("image/png"),
        scaledSize: new window.google.maps.Size(size, size),
        anchor: new window.google.maps.Point(size / 2, size),
      });
    };

    img.onerror = () => {
      // Fallback – solid circle if logo fails to load
      resolve({
        url: canvas.toDataURL("image/png"),
        scaledSize: new window.google.maps.Size(size, size),
        anchor: new window.google.maps.Point(size / 2, size),
      });
    };
  });
}

export default function SentinelAddEscort() {
  const navigate = useNavigate();
  const location = useLocation();
  const skin = useSkin();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const editId = params.get("edit_id") || "";
  const isEdit = Boolean(editId);

  const [form, setForm] = useState(initialForm);
  const [guards, setGuards] = useState([]);

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [activeTarget, setActiveTarget] = useState(null);
  const activeTargetRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState("");

  /* Leaflet refs */
  const leafletMapElementRef = useRef(null);
  const leafletMapInstanceRef = useRef(null);
  const leafletGeocoderRef = useRef(null);
  const leafletFromMarkerRef = useRef(null);
  const leafletToMarkerRef = useRef(null);
  const leafletClickHandlerRef = useRef(null);

  /* Google Maps refs */
  const googleMapElementRef = useRef(null);
  const googleMapInstanceRef = useRef(null);
  const googleGeocoderRef = useRef(null);
  const googleFromMarkerRef = useRef(null);
  const googleToMarkerRef = useRef(null);
  const googleClickListenerRef = useRef(null);

  const didFitBoundsRef = useRef(false);
  const errorShownRef = useRef(false);

  const mapProvider = useMemo(
    () => normalizeMapProvider(dashboardData.user_map),
    [dashboardData.user_map]
  );
  const isGoogleMap = mapProvider === "google";

  const sentinelRoutePage = useMemo(() => "/sentinel_add_Route", []);

  const setTarget = (target) => {
    setActiveTarget(target);
    activeTargetRef.current = target;
    toast(`Click on the map or use search to set: ${target.toUpperCase()} location`, {
      icon: "📍",
      duration: 3000,
    });
  };

  const setLatLngOnTarget = useCallback(
    (target, lat, lng, shouldCenter = false, label = "") => {
      if (!target) return;

      const latNum = Number(lat);
      const lngNum = Number(lng);

      if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) return;

      const lat7 = latNum.toFixed(7);
      const lng7 = lngNum.toFixed(7);

      if (target === "from") {
        setForm((prev) => ({
          ...prev,
          from_lat: lat7,
          from_lng: lng7,
          from_name: label || prev.from_name,
        }));
        toast.success(`From location set: ${label || "Selected location"}`);
      }

      if (target === "to") {
        setForm((prev) => ({
          ...prev,
          to_lat: lat7,
          to_lng: lng7,
          to_name: label || prev.to_name,
        }));
        toast.success(`To location set: ${label || "Selected location"}`);
      }

      if (shouldCenter) {
        if (!isGoogleMap && leafletMapInstanceRef.current) {
          leafletMapInstanceRef.current.invalidateSize(true);
          leafletMapInstanceRef.current.setView([latNum, lngNum], SELECTED_LOCATION_ZOOM);
        }

        if (isGoogleMap && googleMapInstanceRef.current) {
          googleMapInstanceRef.current.panTo({ lat: latNum, lng: lngNum });
          googleMapInstanceRef.current.setZoom(SELECTED_LOCATION_ZOOM);
        }
      }
    },
    [isGoogleMap]
  );

  useEffect(() => {
    const token = getToken();
    const isLoggedIn = localStorage.getItem("isLoggedIn");

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    fetchPageData(token);
  }, [navigate, editId]);

  /* =======================================================
     DESTROY HELPERS
  ======================================================= */
  const destroyLeafletMap = useCallback(() => {
    const map = leafletMapInstanceRef.current;

    if (map && leafletClickHandlerRef.current) {
      map.off("click", leafletClickHandlerRef.current);
    }

    if (map) map.remove();

    leafletMapInstanceRef.current = null;
    leafletGeocoderRef.current = null;
    leafletFromMarkerRef.current = null;
    leafletToMarkerRef.current = null;
    leafletClickHandlerRef.current = null;
    didFitBoundsRef.current = false;
  }, []);

  const destroyGoogleMap = useCallback(() => {
    if (googleClickListenerRef.current) {
      googleClickListenerRef.current.remove();
      googleClickListenerRef.current = null;
    }

    if (googleFromMarkerRef.current) {
      googleFromMarkerRef.current.setMap(null);
      googleFromMarkerRef.current = null;
    }

    if (googleToMarkerRef.current) {
      googleToMarkerRef.current.setMap(null);
      googleToMarkerRef.current = null;
    }

    googleMapInstanceRef.current = null;
    googleGeocoderRef.current = null;
    didFitBoundsRef.current = false;
  }, []);

  /* =======================================================
     SYNC MARKERS
  ======================================================= */
  const syncMarkers = useCallback(async () => {
    const fLat = parseFloat(form.from_lat || "");
    const fLng = parseFloat(form.from_lng || "");
    const tLat = parseFloat(form.to_lat || "");
    const tLng = parseFloat(form.to_lng || "");

    if (!isGoogleMap) {
      const map = leafletMapInstanceRef.current;
      if (!map) return;

      map.invalidateSize(true);

      if (Number.isFinite(fLat) && Number.isFinite(fLng)) {
        if (leafletFromMarkerRef.current) map.removeLayer(leafletFromMarkerRef.current);
        leafletFromMarkerRef.current = L.marker([fLat, fLng], { icon: fromIcon })
          .addTo(map)
          .bindPopup(`From: ${form.from_name || "Selected location"}`);
      } else if (leafletFromMarkerRef.current) {
        map.removeLayer(leafletFromMarkerRef.current);
        leafletFromMarkerRef.current = null;
      }

      if (Number.isFinite(tLat) && Number.isFinite(tLng)) {
        if (leafletToMarkerRef.current) map.removeLayer(leafletToMarkerRef.current);
        leafletToMarkerRef.current = L.marker([tLat, tLng], { icon: toIcon })
          .addTo(map)
          .bindPopup(`To: ${form.to_name || "Selected location"}`);
      } else if (leafletToMarkerRef.current) {
        map.removeLayer(leafletToMarkerRef.current);
        leafletToMarkerRef.current = null;
      }

      const layers = [leafletFromMarkerRef.current, leafletToMarkerRef.current].filter(Boolean);

      if (layers.length > 0) {
        const group = L.featureGroup(layers);
        map.fitBounds(group.getBounds().pad(0.25));
        didFitBoundsRef.current = true;
      } else if (!didFitBoundsRef.current) {
        map.setView([DEFAULT_CENTER.lat, DEFAULT_CENTER.lng], DEFAULT_ZOOM);
      }

      return;
    }

    // ---------- GOOGLE MAPS ----------
    const map = googleMapInstanceRef.current;
    if (!map || !window.google?.maps) return;

    // From (green + logo)
    if (Number.isFinite(fLat) && Number.isFinite(fLng)) {
      const position = { lat: fLat, lng: fLng };
      const fromIconObj = await createGoogleLogoIcon("#16a34a", logo);

      if (googleFromMarkerRef.current) {
        googleFromMarkerRef.current.setPosition(position);
        googleFromMarkerRef.current.setIcon(fromIconObj);
        googleFromMarkerRef.current.setTitle(
          `From: ${form.from_name || "Selected location"}`
        );
      } else {
        googleFromMarkerRef.current = new window.google.maps.Marker({
          map,
          position,
          icon: fromIconObj,
          title: `From: ${form.from_name || "Selected location"}`,
          zIndex: 30,
        });
      }
    } else if (googleFromMarkerRef.current) {
      googleFromMarkerRef.current.setMap(null);
      googleFromMarkerRef.current = null;
    }

    // To (red + logo)
    if (Number.isFinite(tLat) && Number.isFinite(tLng)) {
      const position = { lat: tLat, lng: tLng };
      const toIconObj = await createGoogleLogoIcon("#dc2626", logo);

      if (googleToMarkerRef.current) {
        googleToMarkerRef.current.setPosition(position);
        googleToMarkerRef.current.setIcon(toIconObj);
        googleToMarkerRef.current.setTitle(
          `To: ${form.to_name || "Selected location"}`
        );
      } else {
        googleToMarkerRef.current = new window.google.maps.Marker({
          map,
          position,
          icon: toIconObj,
          title: `To: ${form.to_name || "Selected location"}`,
          zIndex: 30,
        });
      }
    } else if (googleToMarkerRef.current) {
      googleToMarkerRef.current.setMap(null);
      googleToMarkerRef.current = null;
    }

    const positions = [];
    if (googleFromMarkerRef.current) positions.push(googleFromMarkerRef.current.getPosition());
    if (googleToMarkerRef.current) positions.push(googleToMarkerRef.current.getPosition());

    if (positions.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      positions.forEach((pos) => bounds.extend(pos));
      map.fitBounds(bounds, 60);
      didFitBoundsRef.current = true;
    } else if (!didFitBoundsRef.current) {
      map.setCenter(DEFAULT_CENTER);
      map.setZoom(DEFAULT_ZOOM);
    }
  }, [
    form.from_lat,
    form.from_lng,
    form.to_lat,
    form.to_lng,
    form.from_name,
    form.to_name,
    isGoogleMap,
  ]);

  useEffect(() => {
    if (loading) return;
    syncMarkers();
  }, [loading, syncMarkers]);

  /* =======================================================
     INIT: OPENSTREETMAP (LEAFLET)
  ======================================================= */
  useEffect(() => {
    if (loading || isGoogleMap) return undefined;
    if (!leafletMapElementRef.current || leafletMapInstanceRef.current) return undefined;

    setMapError("");
    destroyGoogleMap();

    try {
      delete L.Icon.Default.prototype._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl:
          "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
        iconUrl:
          "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
        shadowUrl:
          "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
      });

      const map = L.map(leafletMapElementRef.current, {
        zoomControl: true,
        attributionControl: true,
      });

      leafletMapInstanceRef.current = map;
      map.setView([DEFAULT_CENTER.lat, DEFAULT_CENTER.lng], DEFAULT_ZOOM);

      L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution: "&copy; OpenStreetMap contributors",
      }).addTo(map);

      const geocoder = L.Control.geocoder({
        defaultMarkGeocode: false,
        position: "topleft",
        placeholder: "Search location...",
        errorMessage: "Location not found",
      })
        .on("markgeocode", (e) => {
          const target = activeTargetRef.current;

          if (!target) {
            toast.error("Please click Pick on Map for From or To first.");
            return;
          }

          const center = e.geocode.center;

          if (e.geocode.bbox) {
            map.fitBounds(e.geocode.bbox);
          } else {
            map.setView(center, SELECTED_LOCATION_ZOOM);
          }

          setLatLngOnTarget(target, center.lat, center.lng, true, e.geocode.name || "");
          toast.success(`Location "${e.geocode.name || "Selected location"}" found and pinned!`);
        })
        .addTo(map);

      leafletGeocoderRef.current = geocoder;

      const handleMapClick = (ev) => {
        const target = activeTargetRef.current;

        if (!target) {
          toast.error("Please click Pick on Map for From or To first.");
          return;
        }

        setLatLngOnTarget(target, ev.latlng.lat, ev.latlng.lng, true);
      };

      map.on("click", handleMapClick);
      leafletClickHandlerRef.current = handleMapClick;

      const resizeMap = () => {
        if (leafletMapInstanceRef.current) {
          leafletMapInstanceRef.current.invalidateSize(true);
        }
      };

      const timers = [
        window.setTimeout(resizeMap, 100),
        window.setTimeout(resizeMap, 500),
        window.setTimeout(resizeMap, 1000),
        window.setTimeout(resizeMap, 1800),
      ];

      window.addEventListener("resize", resizeMap);

      return () => {
        timers.forEach((t) => window.clearTimeout(t));
        window.removeEventListener("resize", resizeMap);
        destroyLeafletMap();
      };
    } catch (error) {
      console.error("OpenStreetMap initialization error:", error);
      setMapError(error.message || "Unable to initialize OpenStreetMap.");
      return undefined;
    }
  }, [loading, isGoogleMap, setLatLngOnTarget, destroyGoogleMap, destroyLeafletMap]);

  /* =======================================================
     INIT: GOOGLE MAPS
  ======================================================= */
  useEffect(() => {
    if (loading || !isGoogleMap) return undefined;
    if (!googleMapElementRef.current || googleMapInstanceRef.current) return undefined;

    let cancelled = false;
    setMapError("");
    setMapLoading(true);
    destroyLeafletMap();

    const initializeGoogleMap = async () => {
      try {
        await loadGoogleMapsScript(GOOGLE_MAPS_API_KEY);
        if (cancelled || !googleMapElementRef.current) return;

        const map = new window.google.maps.Map(googleMapElementRef.current, {
          center: DEFAULT_CENTER,
          zoom: DEFAULT_ZOOM,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true,
          clickableIcons: false,
        });

        googleMapInstanceRef.current = map;
        googleGeocoderRef.current = new window.google.maps.Geocoder();

        googleClickListenerRef.current = map.addListener("click", (ev) => {
          const target = activeTargetRef.current;

          if (!target) {
            toast.error("Please click Pick on Map for From or To first.");
            return;
          }

          const lat = ev.latLng?.lat();
          const lng = ev.latLng?.lng();

          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            setLatLngOnTarget(target, lat, lng, true);
          }
        });

        syncMarkers();
      } catch (error) {
        console.error("Google Maps initialization error:", error);
        const message = getGoogleMapsErrorMessage(error);
        setMapError(message);
        toast.error(message);
      } finally {
        if (!cancelled) setMapLoading(false);
      }
    };

    initializeGoogleMap();

    return () => {
      cancelled = true;
      destroyGoogleMap();
    };
  }, [loading, isGoogleMap, setLatLngOnTarget, destroyGoogleMap, destroyLeafletMap, syncMarkers]);

  async function fetchPageData(token) {
    try {
      setLoading(true);
      errorShownRef.current = false;

      const requests = [
        fetch(api("/api/dashboard/admin/dashboard"), {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(api("/api/sentinel_escort/guards"), {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ];

      if (editId) {
        requests.push(
          fetch(api(`/api/sentinel_escort/${editId}`), {
            headers: { Authorization: `Bearer ${token}` },
          })
        );
      }

      const [dashboardRes, guardsRes, escortRes] = await Promise.all(requests);

      if (dashboardRes.ok) {
        const dashboardJson = await dashboardRes.json();
        const companyDetails = dashboardJson?.data?.companyDetails || {};
        setDashboardData((prev) => ({
          ...prev,
          ...companyDetails,
          user_map: normalizeMapProvider(companyDetails.user_map ?? prev.user_map),
        }));
      }

      if (guardsRes.ok) {
        const guardsJson = await guardsRes.json();
        const raw = guardsJson?.data?.guards ?? guardsJson?.data ?? [];
        setGuards(toGuardsArray(raw));
      } else {
        setGuards([]);
      }

      if (editId) {
        if (!escortRes?.ok) throw new Error("Failed to fetch sentinel escort run details");

        const escortJson = await escortRes.json();
        const run = escortJson?.data?.run || escortJson?.data || {};

        setForm({
          run_id: valueOrEmpty(run.run_id || run.sentinel_run_id || run.id || editId),
          member_id: valueOrEmpty(run.member_id || run.guard_id),
          title: valueOrEmpty(run.title),
          vehicle: valueOrEmpty(run.vehicle),
          start_time: toDatetimeLocal(run.start_time),
          end_time: toDatetimeLocal(run.end_time),
          from_name: valueOrEmpty(run.from_name),
          from_lat: valueOrEmpty(run.start_lat || run.from_lat),
          from_lng: valueOrEmpty(run.start_lng || run.from_lng),
          to_name: valueOrEmpty(run.to_name),
          to_lat: valueOrEmpty(run.end_lat || run.to_lat),
          to_lng: valueOrEmpty(run.end_lng || run.to_lng),
        });
      }
    } catch (error) {
      console.error("Sentinel add escort page error:", error);
      toast.error(error.message || "Failed to load sentinel escort page.");
      errorShownRef.current = true;
      setGuards([]);
    } finally {
      setLoading(false);
    }
  }

  const geocodeAddress = useCallback(
    async (target, text) => {
      const query = String(text || "").trim();

      if (!query) {
        toast.error("Enter a location name first.");
        return;
      }

      setActiveTarget(target);
      activeTargetRef.current = target;

      if (isGoogleMap) {
        try {
          if (!googleGeocoderRef.current) throw new Error("Google Maps is still loading.");

          const response = await googleGeocoderRef.current.geocode({ address: query });
          const firstResult = response?.results?.[0];
          const position = firstResult?.geometry?.location;

          if (!position) throw new Error("Location not found. Please try a different search term.");

          const lat = position.lat();
          const lng = position.lng();
          const label = firstResult?.formatted_address || query;

          setLatLngOnTarget(target, lat, lng, true, label);
          toast.success(`Location "${label}" found and pinned!`);
        } catch (error) {
          console.error("Google search location error:", error);
          toast.error(error.message || "Search location failed. Please try again.");
        }
        return;
      }

      fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(query)}`
      )
        .then((res) => res.json())
        .then((results) => {
          if (!Array.isArray(results) || !results[0]) {
            toast.error("Location not found. Please try a different search term.");
            return;
          }

          const item = results[0];
          const lat = Number(item.lat);
          const lng = Number(item.lon);

          if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            toast.error("Invalid location coordinates.");
            return;
          }

          const label = item.display_name || query;

          setLatLngOnTarget(target, lat, lng, true, label);
          toast.success(`Location "${label}" found and pinned!`);
        })
        .catch((error) => {
          console.error("Search location error:", error);
          toast.error("Search location failed. Please try again.");
        });
    },
    [isGoogleMap, setLatLngOnTarget]
  );

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function validateForm() {
    if (!form.member_id.trim()) return "Guard is required.";
    if (!form.title.trim()) return "Title is required.";
    if (!form.from_name.trim()) return "From location is required.";
    if (!form.to_name.trim()) return "To location is required.";
    if (!form.from_lat || !form.from_lng) return "Please set FROM location on map.";
    if (!form.to_lat || !form.to_lng) return "Please set TO location on map.";
    return "";
  }

  async function handleSubmit(e) {
    e.preventDefault();

    const validationError = validateForm();

    if (validationError) {
      toast.error(validationError);
      return;
    }

    try {
      setSaving(true);
      errorShownRef.current = false;

      const token = getToken();
      const clientId = getClientId();

      const payload = {
        member_id: form.member_id.trim(),
        title: form.title.trim(),
        vehicle: form.vehicle.trim(),
        start_time: form.start_time,
        end_time: form.end_time,
        from_name: form.from_name.trim(),
        from_lat: form.from_lat,
        from_lng: form.from_lng,
        to_name: form.to_name.trim(),
        to_lat: form.to_lat,
        to_lng: form.to_lng,
        client_id: clientId,
      };

      const url = isEdit
        ? api(`/api/sentinel_escort/${form.run_id || editId}`)
        : api("/api/sentinel_escort");

      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to save sentinel escort run");
      }

      if (isEdit) {
        toast.success("Sentinel escort run updated successfully!");
      } else {
        toast.success("Sentinel escort run created successfully!");
        setForm(initialForm);
        setTimeout(() => navigate("/sentinel_escort_list", { replace: true }), 1000);
      }
    } catch (error) {
      console.error("Save sentinel escort error:", error);
      toast.error(error.message || "Failed to save sentinel escort run.");
      errorShownRef.current = true;
    } finally {
      setSaving(false);
    }
  }

  const guardsList = Array.isArray(guards) ? guards : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading sentinel escort management...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={sentinelRoutePage}
      activePage="sentinel-escort"
      activeMenu="sentinel-escort"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
          success: {
            iconTheme: { primary: "#22c55e", secondary: "white" },
          },
          error: {
            iconTheme: { primary: "#ef4444", secondary: "white" },
          },
        }}
      />

      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
          Sentinel Escort Run
        </h1>
        <p className="text-[13px] text-[var(--text-muted)] m-0 mb-[22px]">
          {isEdit ? "Edit an existing sentinel escort run" : "Create a new sentinel escort run"}
        </p>

        {/* Top Card */}
        <div className="relative overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent)]" />
          <div className="flex items-center justify-between gap-3.5 flex-wrap">
            <div>
              <h2 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                {isEdit ? "Edit Sentinel Escort" : "New Sentinel Escort"}
              </h2>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Manage guard, title, vehicle, planned time, and route points
              </p>
            </div>
            <div className="flex gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => navigate(sentinelRoutePage)}
                className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
                style={{
                  background: "var(--btn-bg)",
                  color: "var(--btn-text)",
                  boxShadow: "0 4px 18px var(--accent-glow)",
                }}
              >
                Sentinel Route
              </button>
              <button
                type="button"
                onClick={() => navigate("/sentinel_escort_list")}
                className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5"
              >
                Escort List
              </button>
            </div>
          </div>
        </div>

        {/* Form Section */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="mb-3.5">
            <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
              {isEdit ? "Edit Sentinel Escort Run" : "New Sentinel Escort Run"}
            </h3>
            <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
              Create sentinel escort run and pick from/to coordinates directly from the map
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Guard <span className="text-[var(--red)] ml-0.5">*</span>
                </label>
                <select
                  name="member_id"
                  value={form.member_id}
                  onChange={handleChange}
                  required
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                >
                  <option value="">— Select Guard —</option>
                  {guardsList.map((guard, index) => {
                    const memberId =
                      guard.member_id ||
                      guard.guard_id ||
                      guard.fld_memberId ||
                      guard.fld_memberld ||
                      guard.memberId ||
                      "";

                    const label =
                      guard.label ||
                      guard.guard_name ||
                      guard.fld_name ||
                      guard.fld_username ||
                      guard.username ||
                      memberId;

                    return (
                      <option key={`${memberId}-${index}`} value={memberId}>
                        {label} ({memberId})
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Title <span className="text-[var(--red)] ml-0.5">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  placeholder="e.g., Coimbatore → Erode"
                  required
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Vehicle No.
                </label>
                <input
                  type="text"
                  name="vehicle"
                  value={form.vehicle}
                  onChange={handleChange}
                  placeholder="e.g., TN 66 AB 1234"
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
                />
              </div>

              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Planned Start / End
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="datetime-local"
                    name="start_time"
                    value={form.start_time}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                  />
                  <input
                    type="datetime-local"
                    name="end_time"
                    value={form.end_time}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  From <span className="text-[var(--red)] ml-0.5">*</span>
                </label>
                <div className="space-y-3">
                  <input
                    type="text"
                    name="from_name"
                    value={form.from_name}
                    onChange={handleChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        geocodeAddress("from", form.from_name);
                      }
                    }}
                    placeholder="Search or click on map"
                    required
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={form.from_lat}
                      readOnly
                      placeholder="Lat"
                      className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
                    />
                    <input
                      type="text"
                      value={form.from_lng}
                      readOnly
                      placeholder="Lng"
                      className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
                    />
                  </div>

                  <div className="flex gap-3 flex-wrap">
                    <button
                      type="button"
                      onClick={() => setTarget("from")}
                      className="border-0 rounded-xl px-4 py-2 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:opacity-90"
                      style={{ background: "var(--btn-bg)", color: "var(--btn-text)" }}
                    >
                      Pick on Map
                    </button>
                    <button
                      type="button"
                      onClick={() => geocodeAddress("from", form.from_name)}
                      className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)]"
                    >
                      Search Location
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  To <span className="text-[var(--red)] ml-0.5">*</span>
                </label>
                <div className="space-y-3">
                  <input
                    type="text"
                    name="to_name"
                    value={form.to_name}
                    onChange={handleChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        geocodeAddress("to", form.to_name);
                      }
                    }}
                    placeholder="Search or click on map"
                    required
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={form.to_lat}
                      readOnly
                      placeholder="Lat"
                      className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
                    />
                    <input
                      type="text"
                      value={form.to_lng}
                      readOnly
                      placeholder="Lng"
                      className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
                    />
                  </div>

                  <div className="flex gap-3 flex-wrap">
                    <button
                      type="button"
                      onClick={() => setTarget("to")}
                      className="border-0 rounded-xl px-4 py-2 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:opacity-90"
                      style={{ background: "var(--btn-bg)", color: "var(--btn-text)" }}
                    >
                      Pick on Map
                    </button>
                    <button
                      type="button"
                      onClick={() => geocodeAddress("to", form.to_name)}
                      className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)]"
                    >
                      Search Location
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-5">
              <button
                type="submit"
                disabled={saving}
                className="w-full border-0 rounded-xl px-5 py-3 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                style={{
                  background: "var(--btn-bg)",
                  color: "var(--btn-text)",
                  boxShadow: "0 4px 18px var(--accent-glow)",
                }}
              >
                {saving
                  ? "Saving..."
                  : isEdit
                  ? "Update Sentinel Escort Run"
                  : "Save Sentinel Escort Run"}
              </button>
            </div>
          </form>
        </div>

        {/* Map Section */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <div className="mb-3.5 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                Sentinel Escort Map
              </h3>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Active picker: {activeTarget ? activeTarget.toUpperCase() : "None"}
              </p>
            </div>

            <div className="inline-flex items-center gap-2 rounded-full border border-[var(--surface-border)] bg-[var(--surface)] px-3 py-2">
              <span
                className={`h-2.5 w-2.5 rounded-full ${
                  mapError
                    ? "bg-red-500"
                    : mapLoading
                    ? "animate-pulse bg-amber-500"
                    : "bg-emerald-500"
                }`}
              />
              <span className="text-[12px] font-extrabold text-[var(--text-primary)]">
                {mapError
                  ? "Map Error"
                  : mapLoading
                  ? "Loading Map"
                  : isGoogleMap
                  ? "Google Maps"
                  : "OpenStreetMap"}
              </span>
            </div>
          </div>

          <div className="w-full h-[520px] min-h-[520px] relative overflow-hidden rounded-2xl bg-[var(--surface)] border border-[var(--surface-border)]">
            {!isGoogleMap && (
              <div
                ref={leafletMapElementRef}
                id="sentinel-escort-leaflet-map"
                className="w-full h-[520px] min-h-[520px] block relative z-[1] rounded-2xl overflow-hidden bg-[var(--surface)]"
              />
            )}

            {isGoogleMap && (
              <div
                ref={googleMapElementRef}
                id="sentinel-escort-google-map"
                className="w-full h-[520px] min-h-[520px] block relative z-[1] rounded-2xl overflow-hidden bg-[var(--surface)]"
              />
            )}

            {mapLoading && (
              <div className="absolute inset-0 z-[1000] flex flex-col items-center justify-center gap-3 bg-[var(--card-bg)]/90 backdrop-blur-sm">
                <div className="h-9 w-9 animate-spin rounded-full border-4 border-[var(--accent-faint)] border-t-[var(--accent)]" />
                <p className="text-sm font-extrabold text-[var(--text-primary)]">
                  Loading Google Maps...
                </p>
              </div>
            )}

            {!mapLoading && mapError && (
              <div className="absolute inset-0 z-[1000] flex items-center justify-center bg-[var(--surface)] p-5">
                <div className="w-full max-w-md rounded-2xl border border-red-300 bg-white p-5 text-center shadow-xl">
                  <p className="text-sm font-extrabold text-red-700">Map could not be loaded</p>
                  <p className="mt-2 text-xs font-semibold text-red-600">{mapError}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        #sentinel-escort-leaflet-map .leaflet-container,
        .leaflet-container {
          width: 100% !important;
          height: 520px !important;
          min-height: 520px !important;
          display: block !important;
          background: var(--surface) !important;
          z-index: 1 !important;
        }

        .leaflet-pane { z-index: 2 !important; }
        .leaflet-top, .leaflet-bottom { z-index: 10 !important; }
        .leaflet-control-container { z-index: 20 !important; }

        .custom-map-pin-wrapper {
          background: transparent !important;
          border: none !important;
        }

        .leaflet-control-geocoder {
          border-radius: 12px !important;
          border: 1px solid var(--surface-border) !important;
          background: var(--card-bg) !important;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
          display: flex !important;
          align-items: center !important;
          padding: 4px !important;
          margin: 10px !important;
        }

        .leaflet-control-geocoder-form { display: flex !important; align-items: center !important; }

        .leaflet-control-geocoder input {
          background: var(--surface) !important;
          color: var(--text-primary) !important;
          padding: 8px 12px !important;
          border-radius: 8px !important;
          border: none !important;
          outline: none !important;
          font-size: 13px !important;
          font-family: 'Inter', system-ui, sans-serif !important;
          min-width: 200px !important;
        }

        .leaflet-control-geocoder input::placeholder { color: var(--text-faint) !important; }

        .leaflet-control-geocoder .leaflet-control-geocoder-icon {
          background: var(--btn-bg) !important;
          border-radius: 8px !important;
          width: 32px !important;
          height: 32px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          cursor: pointer !important;
          margin-left: 4px !important;
          border: none !important;
        }

        .leaflet-control-geocoder .leaflet-control-geocoder-icon:before {
          content: "🔍" !important;
          font-size: 14px !important;
          line-height: 1 !important;
        }

        .leaflet-popup-content-wrapper {
          background: var(--card-bg) !important;
          color: var(--text-primary) !important;
          border-radius: 12px !important;
          border: 1px solid var(--card-border) !important;
        }

        .leaflet-popup-tip { background: var(--card-bg) !important; }

        .leaflet-popup-content {
          color: var(--text-primary) !important;
          font-family: 'Inter', system-ui, sans-serif !important;
        }

        .leaflet-control-zoom { margin: 10px !important; }

        .leaflet-control-zoom a {
          background: var(--card-bg) !important;
          color: var(--text-primary) !important;
          border-color: var(--surface-border) !important;
        }

        .leaflet-control-zoom a:hover { background: var(--accent-faint) !important; }
      `}</style>
    </Layout>
  );
}